import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import api from '../lib/api';

type Message = {
  role: 'user' | 'assistant';
  content: string;
  citations?: any[];
  timestamp?: Date;
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: '¡Hola! 👋 Soy tu asistente especializado en productos **Jarad Pet Food**. \n\nPuedo ayudarte con:\n- 🐦 Información sobre alimentación para aves\n- 🐰 Productos para conejos y roedores\n- 🐢 Alimentación para reptiles\n- 📋 Fichas técnicas de productos\n- 🛒 Recomendaciones personalizadas\n\n¿En qué puedo ayudarte hoy?',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState<Set<number>>(new Set());
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMessage: Message = { role: 'user', content: text, timestamp: new Date() };
    const next = [...messages, userMessage];
    setMessages(next);
    setLoading(true);

    try {
      const history = next.map(({ role, content }) => ({ role, content }));
      const res = await api.chat.sendMessage(text, history);

      if (res && res.success && res.data) {
        const { answer, citations } = res.data as { answer: string; citations: any[] };
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: answer,
          citations,
          timestamp: new Date()
        }]);
      } else {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: '😔 Lo siento, ha ocurrido un error. Por favor, intenta reformular tu pregunta.',
          timestamp: new Date()
        }]);
      }
    } catch (e) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: '⚠️ Error de conexión. Por favor, verifica tu conexión a internet e inténtalo de nuevo.',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const sendFeedback = async (messageIndex: number, rating: 'up' | 'down') => {
    if (feedbackSent.has(messageIndex)) return;

    const message = messages[messageIndex];
    const previousMessage = messageIndex > 0 ? messages[messageIndex - 1] : null;

    if (message && previousMessage) {
      try {
        await api.chat.sendFeedback({
          message: previousMessage.content,
          answer: message.content,
          citations: message.citations || [],
          rating
        });
        setFeedbackSent(prev => new Set(prev).add(messageIndex));
      } catch (error) {
        console.error('Error sending feedback:', error);
      }
    }
  };

  const formatTime = (date?: Date) => {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      {/* Botón flotante - Estilo Jarad */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed right-6 bottom-6 z-40 rounded-full bg-black text-white p-4 hover:scale-110 transition-all duration-300 shadow-2xl hover:shadow-black/50"
        aria-label="Abrir chat de asistencia"
      >
        {open ? (
          <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        ) : (
          <div className="relative">
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse"></span>
          </div>
        )}
      </button>

      {/* Ventana de chat - Diseño premium Jarad */}
      {open && (
        <div className="fixed right-6 bottom-24 z-40 w-96 h-[600px] bg-white rounded-3xl flex flex-col overflow-hidden shadow-2xl">
          {/* Header del chat - Negro elegante */}
          <div className="px-6 py-5 bg-black text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                <span className="text-2xl">🐾</span>
              </div>
              <div>
                <div className="text-base font-bold tracking-tight">Asistente Jarad</div>
                <div className="text-xs text-gray-300 flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                  Siempre disponible
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white transition-colors p-1.5 hover:bg-white/10 rounded-lg"
              aria-label="Cerrar chat"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Área de mensajes */}
          <div className="flex-1 overflow-y-auto px-5 py-6 space-y-6 bg-gradient-to-b from-gray-50 to-white">
            {messages.map((m, idx) => (
              <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${m.role === 'user' ? 'order-2' : 'order-1'}`}>
                  <div className={`px-5 py-3 rounded-2xl shadow-sm ${
                    m.role === 'user'
                      ? 'bg-black text-white'
                      : 'bg-white text-gray-900 border border-gray-100'
                  }`}>
                    {m.role === 'assistant' ? (
                      <div className="markdown-content">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({children}) => <p className="mb-2 last:mb-0 text-sm leading-relaxed text-gray-700">{children}</p>,
                            ul: ({children}) => <ul className="list-disc pl-4 mb-2 space-y-1.5">{children}</ul>,
                            ol: ({children}) => <ol className="list-decimal pl-4 mb-2 space-y-1.5">{children}</ol>,
                            li: ({children}) => <li className="text-sm leading-relaxed text-gray-700">{children}</li>,
                            strong: ({children}) => <strong className="font-bold text-black">{children}</strong>,
                            em: ({children}) => <em className="italic">{children}</em>,
                            code: ({children}) => <code className="bg-gray-50 px-1.5 py-0.5 rounded text-xs font-mono text-gray-800 border border-gray-200">{children}</code>,
                            pre: ({children}) => <pre className="bg-gray-50 p-3 rounded-lg overflow-x-auto text-xs border border-gray-200">{children}</pre>,
                            blockquote: ({children}) => <blockquote className="border-l-4 border-black pl-4 italic text-gray-600 my-2">{children}</blockquote>,
                            h1: ({children}) => <h1 className="text-lg font-bold mb-2 text-black">{children}</h1>,
                            h2: ({children}) => <h2 className="text-base font-bold mb-2 text-black">{children}</h2>,
                            h3: ({children}) => <h3 className="text-sm font-bold mb-1 text-black">{children}</h3>,
                            a: ({href, children}) => <a href={href} className="text-black underline hover:text-gray-700 font-medium transition-colors" target="_blank" rel="noopener noreferrer">{children}</a>,
                          }}
                        >
                          {m.content}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <div className="text-sm leading-relaxed">{m.content}</div>
                    )}
                  </div>

                  {/* Timestamp */}
                  <div className={`text-xs text-gray-400 mt-2 px-2 ${m.role === 'user' ? 'text-right' : 'text-left'}`}>
                    {formatTime(m.timestamp)}
                  </div>

                  {/* Solo Feedback para mensajes del asistente - Sources ocultos */}
                  {m.role === 'assistant' && m.citations && m.citations.length > 0 && (
                    <div className="mt-3 px-2">
                      {/* Feedback buttons - se ocultan completamente al hacer clic */}
                      {!feedbackSent.has(idx) && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-medium">¿Te fue útil?</span>
                          <button
                            className="px-3 py-1.5 text-xs rounded-full bg-white border border-gray-200 hover:bg-black hover:text-white hover:border-black text-gray-700 transition-all duration-200 font-medium"
                            onClick={() => sendFeedback(idx, 'up')}
                          >
                            👍 Sí
                          </button>
                          <button
                            className="px-3 py-1.5 text-xs rounded-full bg-white border border-gray-200 hover:bg-black hover:text-white hover:border-black text-gray-700 transition-all duration-200 font-medium"
                            onClick={() => sendFeedback(idx, 'down')}
                          >
                            👎 No
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 rounded-2xl px-5 py-3 shadow-sm">
                  <div className="flex space-x-1.5">
                    <div className="w-2.5 h-2.5 bg-black rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2.5 h-2.5 bg-black rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2.5 h-2.5 bg-black rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          <div className="p-5 border-t border-gray-100 bg-white">
            <div className="flex items-end gap-3">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black focus:ring-1 focus:ring-black resize-none min-h-[44px] max-h-[120px] transition-all duration-200"
                placeholder="Escribe tu pregunta sobre productos Jarad..."
                rows={1}
                style={{ height: 'auto' }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = Math.min(target.scrollHeight, 120) + 'px';
                }}
              />
              <button
                onClick={send}
                disabled={loading || !input.trim()}
                className="px-4 py-3 text-sm font-semibold rounded-xl bg-black text-white hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                aria-label="Enviar mensaje"
              >
                {loading ? (
                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                )}
              </button>
            </div>
            <div className="mt-3 text-xs text-gray-400 text-center font-medium">
              Presiona Enter para enviar • Shift+Enter para nueva línea
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;